<template>
    <div
        v-if="checking"
        class="min-h-screen flex items-center justify-center bg-white dark:bg-taupe-900"
        data-testid="welcome-screen-checking"
    >
        <img
            src="https://cdn.zenmanage.com/images/spinner-1s-200px-taupe-500.png"
            alt="[Spinner]"
            class="h-8 w-8"
        />
    </div>

    <branded-split-screen
        v-else
        data-testid="welcome-screen"
        heading="Welcome aboard!"
        description="Take a moment to tell us a bit about yourself. We'll use this to tailor your experience."
        :highlights="highlights"
        footnote="All questions are optional — skip any you'd prefer not to answer."
    >
        <h2 class="text-2xl font-semibold text-taupe-900 dark:text-taupe-50">
            A few quick questions
        </h2>
        <p class="mt-1 text-sm text-taupe-500 dark:text-taupe-400">
            All answers are optional.
        </p>

        <form @submit.prevent="submit" class="mt-8 space-y-6">
            <!-- Role -->
            <div>
                <label
                    class="block text-sm font-medium text-taupe-700 dark:text-taupe-200 mb-1.5"
                    for="survey-role"
                >
                    What's your role?
                </label>
                <select
                    id="survey-role"
                    v-model="form.persona_role"
                    class="w-full rounded-lg border border-taupe-300 bg-white px-3 py-2.5 text-sm text-taupe-900 dark:text-taupe-50 shadow-xs focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 dark:border-taupe-600 dark:bg-taupe-800"
                >
                    <option value="">— Select —</option>
                    <option value="engineer">Engineer</option>
                    <option value="engineering_manager">
                        Engineering Manager
                    </option>
                    <option value="product">Product</option>
                    <option value="qa">QA</option>
                    <option value="other">Other</option>
                </select>
            </div>

            <!-- Use case -->
            <div>
                <label
                    class="block text-sm font-medium text-taupe-700 dark:text-taupe-200 mb-1.5"
                    for="survey-use-case"
                >
                    What's your primary use case?
                </label>
                <select
                    id="survey-use-case"
                    v-model="form.persona_use_case"
                    class="w-full rounded-lg border border-taupe-300 bg-white px-3 py-2.5 text-sm text-taupe-900 dark:text-taupe-50 shadow-xs focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 dark:border-taupe-600 dark:bg-taupe-800"
                >
                    <option value="">— Select —</option>
                    <option value="feature_rollouts">Feature rollouts</option>
                    <option value="kill_switches">Kill switches</option>
                    <option value="ab_testing">A/B testing</option>
                    <option value="trunk_based_dev">
                        Trunk-based development
                    </option>
                    <option value="exploring">Just exploring</option>
                </select>
            </div>

            <!-- Team size -->
            <div>
                <label
                    class="block text-sm font-medium text-taupe-700 dark:text-taupe-200 mb-1.5"
                    for="survey-team-size"
                >
                    How large is your engineering team?
                </label>
                <select
                    id="survey-team-size"
                    v-model="form.persona_team_size"
                    class="w-full rounded-lg border border-taupe-300 bg-white px-3 py-2.5 text-sm text-taupe-900 dark:text-taupe-50 shadow-xs focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 dark:border-taupe-600 dark:bg-taupe-800"
                >
                    <option value="">— Select —</option>
                    <option value="1">Just me</option>
                    <option value="2-10">2–10</option>
                    <option value="11-50">11–50</option>
                    <option value="51-200">51–200</option>
                    <option value="200+">200+</option>
                </select>
            </div>

            <!-- Referral source -->
            <div>
                <label
                    class="block text-sm font-medium text-taupe-700 dark:text-taupe-200 mb-1.5"
                    for="survey-referral"
                >
                    How did you hear about us?
                </label>
                <input
                    id="survey-referral"
                    type="text"
                    v-model="form.persona_referral_source"
                    placeholder="e.g. Google, a colleague, Hacker News…"
                    class="w-full rounded-lg border border-taupe-300 bg-white px-3 py-2.5 text-sm text-taupe-900 dark:text-taupe-50 shadow-xs placeholder-taupe-400 dark:placeholder-taupe-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 dark:border-taupe-600 dark:bg-taupe-800"
                    maxlength="255"
                />
            </div>

            <div
                v-if="error"
                class="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 dark:bg-red-950 dark:border-red-900 dark:text-red-300"
                data-testid="survey-error"
            >
                {{ error }}
            </div>

            <!-- Actions -->
            <div class="flex items-center justify-between pt-2">
                <zen-button
                    type="button"
                    variant="link-muted"
                    class="dark:text-taupe-400 dark:hover:text-taupe-200"
                    :disabled="loading"
                    data-testid="survey-skip"
                    @click="skip"
                >
                    Skip for now
                </zen-button>
                <zen-button
                    type="submit"
                    variant="primary"
                    size="medium"
                    :processing="loading"
                    processing-label="Saving…"
                    data-testid="survey-submit"
                >
                    Let's go →
                </zen-button>
            </div>
        </form>
    </branded-split-screen>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import BrandedSplitScreen from '@/screens/_components/BrandedSplitScreen.vue'
import { useRouter } from 'vue-router'
import { useOnboardingStore } from '@/stores/onboarding'
import { useSessionStore } from '@/stores/session'
import { BRAND_HIGHLIGHTS } from '@/utils/brand-highlights'

const router = useRouter()
const onboardingStore = useOnboardingStore()
const sessionStore = useSessionStore()

const checking = ref(true)
const loading = ref(false)
const error = ref('')
const form = ref({
    persona_role: '',
    persona_use_case: '',
    persona_team_size: '',
    persona_referral_source: '',
})

const highlights = BRAND_HIGHLIGHTS

function getRedirectDestination() {
    const redirectPath = sessionStorage.getItem('redirectAfterLogin')
    sessionStorage.removeItem('redirectAfterLogin')
    if (redirectPath) return redirectPath

    const current = sessionStore.data?.current
    if (current?.projectKey && current?.environmentKey) {
        return { name: 'ProjectFlagsScreen', params: current }
    }
    return { name: 'ProjectFlagsScreen' }
}

onMounted(async () => {
    const accountUlid = sessionStore.data?.account?.data?.ulid
    if (!onboardingStore.loaded && accountUlid) {
        await onboardingStore.fetch(accountUlid)
    }
    if (onboardingStore.surveyCompleted) {
        router.replace(getRedirectDestination())
        return
    }
    checking.value = false
})

async function submit() {
    loading.value = true
    error.value = ''
    const user = sessionStore.data?.user?.data
    try {
        await sessionStore.updateUser({
            first_name: user?.first_name,
            last_name: user?.last_name,
            email: user?.email,
            allow_marketing_emails: user?.allow_marketing_emails ?? false,
            persona_role: form.value.persona_role || null,
            persona_use_case: form.value.persona_use_case || null,
            persona_team_size: form.value.persona_team_size || null,
            persona_referral_source: form.value.persona_referral_source || null,
        })
        await onboardingStore.markComplete('survey_completed')
        await router.push(getRedirectDestination())
    } catch (e) {
        error.value =
            e?.message ||
            (typeof e === 'string'
                ? e
                : 'Something went wrong. Please try again.')
        loading.value = false
    }
}

async function skip() {
    loading.value = true
    error.value = ''
    try {
        await onboardingStore.markComplete('survey_completed')
        await router.push(getRedirectDestination())
    } catch {
        error.value = "Couldn't skip that — try again."
        loading.value = false
    }
}
</script>
